import { Box, Card, CardContent, Typography } from '@mui/material';
import type { Sheet, SheetCell } from '../../api/managementReports';

const tooltip = (c: SheetCell) =>
  c.kind === 'formula'
    ? `${c.address}: ${c.formula}`
    : c.kind === 'data'
      ? `${c.address}: ${c.value === null ? 'blank — the CRM doesn’t hold this yet' : 'from the CRM'}`
      : c.address;

/** The KPI workbook's sheet, drawn cell by cell as in Excel: the same
 * merged cells, fills, bold text, alignment and number formats (worked out
 * on the server). Wide sheets scroll inside the card on small screens. */
export const SheetView = ({ sheet }: { sheet: Sheet }) => {
  const total = sheet.columns.reduce((n, c) => n + c.width, 0);
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            aria-label="KPI workbook sheet"
            sx={{
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              width: '100%',
              minWidth: 880,
              fontFamily: 'Calibri, Carlito, "Segoe UI", Arial, sans-serif',
              fontSize: 14,
              color: '#000',
              bgcolor: '#fff',
              '& td': { border: '1px solid #b7b7b7', px: 1, py: 0.5, verticalAlign: 'middle', overflowWrap: 'normal', hyphens: 'manual' },
            }}
          >
            <colgroup>
              {sheet.columns.map((c) => (
                <col key={c.letter} style={{ width: `${(c.width / total) * 100}%` }} />
              ))}
            </colgroup>
            <tbody>
              {sheet.rows.map((row, r) => (
                <tr key={r} style={row.height ? { height: Math.round(row.height * 1.33) } : undefined}>
                  {row.cells.map((c) =>
                    c ? (
                      <td
                        key={c.address}
                        data-address={c.address}
                        data-kind={c.kind}
                        rowSpan={c.rowSpan}
                        colSpan={c.colSpan}
                        title={tooltip(c)}
                        style={{
                          background: c.fill ?? '#fff',
                          color: c.color,
                          fontWeight: c.bold ? 700 : 400,
                          // Workbook points → the sheet's 14px-for-12pt scale.
                          fontSize: c.size ? (c.size / 12) * 14 : undefined,
                          textAlign: c.align === 'center' || c.align === 'right' ? c.align : typeof c.value === 'number' ? 'right' : 'left',
                          whiteSpace: typeof c.value === 'number' ? 'nowrap' : undefined,
                        }}
                      >
                        {c.text}
                      </td>
                    ) : null,
                  )}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Exactly as the KPI workbook. Blank cells are data the CRM doesn’t hold yet. Hover a cell to see its formula or where its value comes from.
        </Typography>
      </CardContent>
    </Card>
  );
};
